import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  attachGeneration,
  retryFpEngagement,
} from '@/shared/components/navigator-results/generation/navigatorGenerationStore';
import { useNavigatorGeneration } from '@/shared/components/navigator-results/generation/useNavigatorGeneration';
import type {
  GenerationMode,
  GenerationPhase,
  NavigatorGenerationState,
} from '@/shared/components/navigator-results';
import { trackRumAction } from '@/shared/observability/rum';
import { CareGuideLoadingVisual } from './CareGuideLoadingVisual';
import { GENERATION_STEPS, careGuideLoadingCopy } from './careGuideLoadingCopy';
import { readGeneratingSnapshot } from '../hooks/navigatorChatCompletion';

type GeneratingLocationState = {
  sessionId?: string;
  careStage?: string;
  summaryBullets?: string[];
  mode?: GenerationMode;
};

// Hold on the ready checkmark before auto-advancing (prototype AUTO_ADVANCE_MS).
const AUTO_ADVANCE_MS = 1100;
// Minimum time each phase label stays on screen so the story stays readable
// even when orchestrator steps complete in quick succession.
const MIN_PHASE_DWELL_MS = 1000;

const stepIndexForPhase = (phase: GenerationPhase | undefined): number => {
  // No store state yet (cold mount, before attach) → start at the first step;
  // 'done' pins to the last one.
  if (!phase) return 0;
  if (phase === 'done') return GENERATION_STEPS.length - 1;
  const index = GENERATION_STEPS.findIndex((step) => step.phase === phase);
  return index === -1 ? 0 : index;
};

type MatchingProgress = NonNullable<NavigatorGenerationState['matchingProgress']>;

const buildPhaseLabel = (
  displayIndex: number,
  mode: GenerationMode,
  progress: MatchingProgress | null,
): string => {
  const step = GENERATION_STEPS[displayIndex];
  const label = step.label[mode];
  // Live counter only once something actually matched — "0 of 4" reads stalled.
  if (step.phase === 'matching' && progress && progress.matched > 0) {
    return `${label} · ${Math.min(progress.matched, progress.total)} of ${progress.total}`;
  }
  return label;
};

const computeProgressTarget = (
  displayIndex: number,
  isDone: boolean,
  progress: MatchingProgress | null,
): number => {
  if (isDone) return 100;
  const step = GENERATION_STEPS[displayIndex];
  // Matching interpolates on real specialty completions between the searching
  // and matching targets instead of the fake ease-toward-target.
  if (step.phase === 'matching' && progress && progress.total > 0) {
    const base = GENERATION_STEPS[displayIndex - 1]?.target ?? 8;
    const ratio = Math.min(progress.matched, progress.total) / progress.total;
    return Math.round(base + ratio * (step.target - base));
  }
  // Ease toward just below the active step's target; snap to 100 on ready.
  return Math.max(8, step.target - 6);
};

/** Monotonic display step: advances one at a time with a minimum dwell. */
const useDisplayStep = (targetIndex: number): number => {
  const [displayIndex, setDisplayIndex] = useState(targetIndex);
  const lastAdvanceRef = useRef(Date.now());

  useEffect(() => {
    if (targetIndex <= displayIndex) return;
    const wait = Math.max(0, MIN_PHASE_DWELL_MS - (Date.now() - lastAdvanceRef.current));
    const timer = setTimeout(() => {
      lastAdvanceRef.current = Date.now();
      setDisplayIndex((index) => Math.min(targetIndex, index + 1));
    }, wait);
    return () => clearTimeout(timer);
  }, [targetIndex, displayIndex]);

  return displayIndex;
};

/**
 * Guided loading page (/care-navigator/generating). Generation was already
 * kicked by the chat (startGeneration); this page attaches, narrates the real
 * pipeline phases, and auto-advances when the guide is ready — care seekers
 * and guests to /care-navigator/results, FPs to the dashboard results page.
 */
export const CareGuideLoadingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as GeneratingLocationState | null) ?? {};
  // Durable fallback for a reload / direct hit on this URL, which drops
  // router state (the FP flow has no other persisted session/mode — the
  // legacy bquest_nav_session_id/care_stage keys below are care-seeker only).
  // Computed once at mount: location.state doesn't change post-navigation.
  const [snapshot] = useState(() => (state.sessionId ? null : readGeneratingSnapshot()));

  const sessionId =
    state.sessionId ??
    snapshot?.sessionId ??
    sessionStorage.getItem('bquest_nav_session_id') ??
    undefined;
  const careStage =
    state.careStage ?? snapshot?.careStage ?? sessionStorage.getItem('bquest_nav_care_stage') ?? '';
  const summaryBullets = state.summaryBullets ?? snapshot?.summaryBullets ?? [];
  const fpEngagement = snapshot?.fpEngagement;

  const generation = useNavigatorGeneration(sessionId);
  const mode: GenerationMode = state.mode ?? snapshot?.mode ?? generation?.mode ?? 'care-seeker';

  const status = generation?.status ?? 'running';
  const engagementId = generation?.engagementId ?? null;
  // 'error' advances too — the results page owns the degraded empty state,
  // exactly as it did when it ran the pipeline itself.
  const isDone = status === 'ready' || status === 'error';
  // Hand over once there is a top match to show — a guide with a real featured
  // provider, not an empty shell. The remaining specialties fill in per row on
  // the results page. Phase 2 runs specialties in display order, so the first
  // to land is the primary; `some` rather than `[0]` keeps the page from
  // hanging when the primary itself comes back with no providers.
  // Note: the AI "why this match" copy arrives later (backend Phase 3 runs
  // after every specialty), so it fills in after the handover.
  const hasTopMatch = (generation?.categories ?? []).some(
    (category) => category.providers.length > 0,
  );
  const canAdvance = (isDone || hasTopMatch) && (mode !== 'fp' || Boolean(engagementId));

  useEffect(() => {
    if (!sessionId) return;
    // Idempotent: no-ops when the chat already started generation; re-kicks
    // the matching-only pipeline after a reload. Passing fpEngagement only
    // when recovered from the snapshot keeps the common (non-reload) call
    // shape unchanged (two args).
    if (fpEngagement) {
      attachGeneration(sessionId, mode, fpEngagement);
    } else {
      attachGeneration(sessionId, mode);
    }
    trackRumAction('care_guide_loading_viewed', { sessionId, mode });
    // fpEngagement comes from `snapshot`, computed once at mount — stable
    // across re-renders, safe as a dependency.
  }, [sessionId, mode, fpEngagement]);

  const displayIndex = useDisplayStep(stepIndexForPhase(generation?.phase));
  const matchingProgress = generation?.matchingProgress ?? null;
  const phaseLabel = buildPhaseLabel(displayIndex, mode, matchingProgress);
  const progressTarget = computeProgressTarget(displayIndex, isDone, matchingProgress);

  useEffect(() => {
    if (!canAdvance || !sessionId) return;
    const timer = setTimeout(() => {
      trackRumAction('care_guide_auto_advance', { sessionId, mode });
      if (mode === 'fp') {
        navigate(`/dashboard/financial-advisor?fp_qa_engagement=${engagementId}`, {
          replace: true,
        });
      } else {
        navigate('/care-navigator/results', {
          replace: true,
          state: { sessionId, careStage, summaryBullets },
        });
      }
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
    // summaryBullets/careStage are stable for a mounted session; keying the
    // effect on them (fresh array identity per render) would reset the hold.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAdvance, engagementId, mode, sessionId, navigate]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto w-full max-w-3xl space-y-6">
            <button
              type="button"
              onClick={() => navigate('/care-navigator')}
              className="inline-flex items-center gap-1 text-sm text-sage transition-colors duration-150 ease-out hover:text-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to Navigator
            </button>
            <div className="rounded-2xl border border-dashed border-border-subtle bg-card-subtle p-8 text-center">
              <p className="font-termina text-xl font-medium text-text-primary">
                This care guide session has expired
              </p>
              <p className="mt-2 text-sm text-text-secondary">
                Head back to the Navigator to start a new one.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const engagementNotice =
    mode === 'fp' && generation?.engagementError ? (
      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-left">
        <p className="text-xs text-red-700">
          We couldn&apos;t save this care guide to your dashboard.
        </p>
        <button
          type="button"
          onClick={() => retryFpEngagement(sessionId)}
          className="whitespace-nowrap rounded-full border border-red-300 px-3 py-1 text-xs font-medium text-red-700 transition-colors duration-150 ease-out hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    ) : undefined;

  return (
    <div className="min-h-screen bg-background">
      <CareGuideLoadingVisual
        copy={careGuideLoadingCopy[mode]}
        phaseLabel={phaseLabel}
        progressTarget={progressTarget}
        isReady={isDone}
        notice={engagementNotice}
      />
    </div>
  );
};
